import type { OrderDto } from './orders.service';
import { getPromoDiscountAmountInCents } from '@/utils/order-pricing';

const formatCsvMoney = (value?: { amount: number; currency: string }) => {
  if (!value || !Number.isFinite(value.amount) || value.amount <= 0) {
    return '';
  }

  return `${(value.amount / 100).toFixed(2)} ${value.currency}`.trim();
};

const escapeCsvValue = (value: string | number | null | undefined) => {
  if (value == null) {
    return '';
  }

  const normalized = String(value).replace(/\r?\n|\r/g, ' ');
  return `"${normalized.replace(/"/g, '""')}"`;
};

export const buildOrdersCsv = (orders: OrderDto[]) => {
  const headers = [
    'ID',
    'Reference',
    'Passes',
    'Ski Days',
    'Start Date',
    'Domain',
    'Status',
    'Client Price',
    'Promo code',
    'Discount',
    'Supplier Price',
    'Margin',
    'Created At',
    'Paid At',
    'Customer Email',
  ];

  const rows = orders.map((order) => {
    const passCount = order.passes > 0
      ? order.passes
      : order.tripDetails
        ? order.tripDetails.skierCounts.reduce((sum, count) => sum + count.quantity, 0)
        : 0;

    const domains = order.domains.map((domain) => domain.name).join(', ');
    const status = order.status.charAt(0).toUpperCase() + order.status.slice(1);
    const discountAmount = getPromoDiscountAmountInCents(order);
    const discountCurrency = order.clientPrice?.currency || order.supplierPrice?.currency || '';

    return [
      order.orderId,
      order.reference,
      passCount,
      order.skiDays,
      order.startDate,
      domains,
      status,
      formatCsvMoney(order.clientPrice),
      order.promoCode ?? '',
      formatCsvMoney({ amount: discountAmount, currency: discountCurrency }),
      formatCsvMoney(order.supplierPrice),
      formatCsvMoney(order.margin),
      order.createdAt,
      order.paidAt ?? '',
      order.customerEmail ?? '',
    ].map(escapeCsvValue).join(',');
  });

  return [headers.map(escapeCsvValue).join(','), ...rows].join('\n');
};

export const downloadOrdersCsv = (orders: OrderDto[], options?: { isFiltered?: boolean }) => {
  const csvContent = buildOrdersCsv(orders);
  const timestamp = new Date().toISOString().slice(0, 10);
  const suffix = options?.isFiltered ? '-filtered' : '';
  const fileName = `orders-${timestamp}${suffix}.csv`;
  const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};